import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [hasBackground, setHasBackground] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Now that we removed custom scroll container, use window.scrollY
      const scrollY = window.scrollY || window.pageYOffset;
      const shouldHaveBackground = scrollY > 20;
      
      setHasBackground(shouldHaveBackground);
      console.log('Scroll position:', scrollY, 'Has background:', shouldHaveBackground);
    };

    // Add scroll listener to window
    window.addEventListener('scroll', handleScroll);
    
    // Check initial position
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  return (
    <nav className="fixed top-0 w-full z-50 px-4 py-4">
      <div 
        className={`max-w-7xl mx-auto transition-all duration-300 rounded-2xl ${
          hasBackground 
            ? 'bg-white shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <div className="px-6 sm:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={closeMenu}>
              <img 
                src="/assets/fss-logo.png" 
                alt="Functional Safety Systems Logo" 
                className={`h-10 w-auto cursor-pointer transition-all duration-300 ${
                  !hasBackground ? 'brightness-0 invert' : ''
                }`}
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link
                  to="/about"
                  className={`${
                    hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                  } px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300`}
                >
                  About
                </Link>
                
                {/* Services Dropdown */}
                <div className="relative">
                  <button
                    onClick={toggleServices}
                    className={`${
                      hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                    } px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300 flex items-center`}
                  >
                    Services
                    <svg className="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isServicesOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                    </svg>
                  </button>
                  
                  {isServicesOpen && (
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1">
                        <Link
                          to="/trainings"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={closeMenu}
                        >
                          Trainings
                        </Link>
                        <Link
                          to="/certifications"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={closeMenu}
                        >
                          Certifications
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <Link
                  to="/past-projects"
                  className={`${
                    hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                  } px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300`}
                >
                  Past Projects
                </Link>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className={`inline-flex items-center justify-center p-2 rounded-md ${
                  hasBackground 
                    ? 'text-gray-900 hover:bg-gray-100' 
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  hasBackground ? 'focus:ring-offset-white focus:ring-gray-500' : 'focus:ring-offset-transparent focus:ring-white'
                }`}
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/about"
              className={`${
                hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
              } block px-3 py-2 rounded-md text-base font-medium`}
              onClick={closeMenu}
            >
              About
            </Link>
            
            {/* Mobile Services Section */}
            <div>
              <button
                onClick={toggleServices}
                className={`${
                  hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                } px-3 py-2 rounded-md text-base font-medium w-full text-left flex items-center justify-between`}
              >
                Services
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isServicesOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
              </button>
              {isServicesOpen && (
                <div className="pl-6">
                  <Link
                    to="/trainings"
                    className={`${
                      hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                    } block px-3 py-2 rounded-md text-base font-medium`}
                    onClick={closeMenu}
                  >
                    Trainings
                  </Link>
                  <Link
                    to="/certifications"
                    className={`${
                      hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
                    } block px-3 py-2 rounded-md text-base font-medium`}
                    onClick={closeMenu}
                  >
                    Certifications
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/past-projects"
              className={`${
                hasBackground ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white hover:bg-opacity-20'
              } block px-3 py-2 rounded-md text-base font-medium`}
              onClick={closeMenu}
            >
              Past Projects
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;